import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, autorun, html2canvas, Fragment, Resizable, toJS} from '@api'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {ResizableBox, ResizeHandle} from 'react-resizable'
import {PresentationPageTitle, PresentationPageGroupContent, PresentationPageGroupContentStore} from '../'
import {IPresentationCatalogPage, StoresInstance} from '@stores'

import * as css from './PresentationPageView.css'

/// region Store

export class PresentationPageViewStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  get thumbnail() { return this.props.thumbnail}

  get page() { return this.props.page}

  get store() { return this.catalog.componentStores.presentation}

  private htmlCanvas: HTMLCanvasElement

  _updateCanvasDebounced = _.debounce(async () => {
    const {ref, page, store: {settings: {zoom}}, componentStores: {groupContent}} = this

    if (!page.size) {
      const {width, height} = ref.getBoundingClientRect()
      //debugger
      // console.log({width, height})

      this.page.size = {width: width / zoom, height: height / zoom}
    }

    // Needs CORS support to CF to work
    return
    const canvas = await html2canvas(ref, {allowTaint: false, useCORS: false})
    this.htmlCanvas = canvas
    document.body.append(canvas)

    const context = canvas.getContext('2d'),
      imgDataUrl = canvas.toDataURL('image/png')

    page.snapshotImage = imgDataUrl
    canvas.remove()
  }, 100)

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {props: {thumbnail}, _updateCanvasDebounced} = this
        if (!thumbnail) {
          const {ref, componentStores: {groupContent}} = this
          if (ref && groupContent?.childGroupsLoaded) {
            _updateCanvasDebounced()
          }
        }
      }),
      autorun(() => {
        const {props: {thumbnail}, ref, isCurrentPage} = this
        if (thumbnail && ref && isCurrentPage) {
          ref.scrollIntoView({block: 'center', inline: 'center'})
        }
      })
    ]
    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @computed get itemCountLabel() {
    const {pageGroupNode} = this
    if (pageGroupNode?.store) {
      return pageGroupNode.store.totalCount.toLocaleString()
    }

    return null
  }

  @observable.shallow componentStores: {
    groupContent?: PresentationPageGroupContentStore
  } = {}

  @computed get isCurrentPage() {
    return this.page === this.store.page
  }

  @observable.ref ref: HTMLDivElement
  setRef = r => this.ref = r

  @observable.ref contentRef: HTMLDivElement
  setContentRef = r => {
    this.contentRef = r

    if (r && !this.thumbnail) {
      const {page: {size}} = this
      if (size) {
        r.style.width = `${size.width}px`
        r.style.height = `${size.height}px`
      }
    }
  }

  @computed get cssStyle() {
    const {page, thumbnail, store: {translate, settings: {zoom}}} = this

    if (!page || thumbnail) return {}

    return {
      // aspectRatio: `${width} / ${height}`,
      transform: `translate(${translate?.x ?? 0}px, ${translate?.y ?? 0}px) scale(${zoom})`
      // maxWidth: `${width * zoom}px`,
      // maxHeight: `${height * zoom}px`
    }
  }

  @computed get pageGroupNode() {
    const {page: {info}, groupBy: {firstGroup: {nodes}}} = this
    if (info?.group) {
      return nodes.get(info.group)
    }

    return null
  }

  @computed get pageTitle(): JSX.Element | string {
    const {page} = this
    if (!page) return null

    switch (page.info?.type) {
      case 'group': {
        const {pageGroupNode} = this
        return !pageGroupNode?.store ? null : pageGroupNode.store.groupLabel
      }

      default: {
        return page.title
      }
    }
  }

  @computed get pageSubTitles() {
    const {page} = this
    if (!page) return null

    switch (page.info?.type) {
      case 'group': {
        const {pageGroupNode} = this
        return !pageGroupNode?.store ? [] : pageGroupNode.store.mySubtitleFieldsWithValues
      }

      default: {
        return []
      }
    }
  }

  @action onClick = async () => {
    const {props: {thumbnail}} = this
    if (thumbnail) {
      this.store.page = this.page
    }
    // else {
    //   if (!this.hasCapturedScreenshot) {
    //     this.hasCapturedScreenshot = true
    //     const {ref: {clientWidth, clientHeight}} = this,
    //       stream = await navigator.mediaDevices.getDisplayMedia({video: {frameRate: 1, width: clientWidth, height: clientHeight}, audio: false}),
    //       track = stream.getVideoTracks()[0]
    //
    //     const video = document.createElement('video') as HTMLVideoElement
    //     video.srcObject = stream;
    //     video.onloadedmetadata = e => {
    //       video.play();
    //       video.pause();
    //
    //       debugger
    //     }
    //   }
    // }
  }

  // @observable hasCapturedScreenshot = false

  @action onResize = (e, data) => {
     // console.log(data.size)
    // this._throttleSizeUpdate(data.size)
    _.assign(this.page.size, data.size)
    this.contentRef.style.width = `${data.size.width}px`
    this.contentRef.style.height = `${data.size.height}px`
  }

  _throttleSizeUpdate = _.throttle((size: { width: number, height: number }) => {
    this.page.size = size
  }, 50, { leading: false})

  @observable lastMousePoint: { screenX: number, screenY: number }
  @action onMouseDown = ({target, currentTarget, screenX, screenY}: React.MouseEvent) => {
    if (!(target as HTMLDivElement).classList.contains('react-resizable-handle')) {
      this.lastMousePoint = {screenX, screenY}
    }
  }

  @action onMouseUp = () => {
    this.lastMousePoint = null
  }

  @computed get isMoving() {
    const {lastMousePoint: lmp, store: {canZoomOut}} = this

    return canZoomOut && lmp != null
  }

  @action onMouseMove = ({screenX, screenY, buttons}: React.MouseEvent) => {
    const {lastMousePoint: lmp, store, store: {page, translate}, isMoving} = this

    if (isMoving) {
      if (buttons !== 1) {
        this.lastMousePoint = null
      } else {
        store.translate ={
            x: (translate?.x ?? 0) + screenX - lmp.screenX,
            y: (translate?.y ?? 0) + screenY - lmp.screenY
          }

        _.assign(this.lastMousePoint, {screenX, screenY})

        // console.log(toJS(page.translate))
      }
    }
  }

  @action onDoubleClick = () => {
    if (this.isCurrentPage && !this.thumbnail) {
      this.store.settings.zoom = this.store.defaultZoom // todo - this needs to be zoom-to-fit like
    }
  }

  @action onWheel = (e: React.WheelEvent) => {
    if (this.isCurrentPage && !this.thumbnail) {
      if (e.ctrlKey) { // Trackpad pinch
        const {ctrlKey, deltaX, deltaY, deltaZ} = e,
          {store: {pinchZoom_throttled}} = this
        pinchZoom_throttled(deltaY < 0)
      }
    }
  }

  resizeHandles : ResizeHandle[] = ['ne', 'nw', 'sw', 'se']
}

export const PageViewStoreContext = React.createContext<PresentationPageViewStore>(null)
export const usePresentationPageViewStore = () => React.useContext(PageViewStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  page?: IPresentationCatalogPage
  thumbnail?: boolean
}

export const PresentationPageView = observer<MyProps>(function PresentationPageView({className, ...props}) {
  const obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new PresentationPageViewStore(stores, obsProps)),
    {cssStyle, setContentRef, resizeHandles, page: {snapshotImage, size}, onResize, props: {thumbnail}, setRef, onClick, isCurrentPage, onMouseDown, onMouseMove, onMouseUp, onWheel, onDoubleClick} = store

  useEffect(() => store.registerEffects(), [])

  const contents = <div className={css.contents}
                        ref={setContentRef}
                        // style={size ? { maxWidth: size.width, maxHeight: size.height} : undefined}
                        onClick={thumbnail ? onClick : undefined}
    //data-moving={isMoving}
    // onClick={utils.eatMouseEvent}
                        onMouseDown={onMouseDown}
                        onMouseUp={onMouseUp}
                        onDoubleClick={onDoubleClick}
                        onMouseMove={onMouseMove}>
    <PresentationPageTitle/>
    {!thumbnail && <PresentationPageContent/>}
  </div>

  // console.log(toJS({interimSize, size}, {recurseEverything: true}))

  return (
    <PageViewStoreContext.Provider value={store}>
      <div
        className={classNames(css.root, className)}
        data-auto-size={size == null}
        ref={setRef}
        style={cssStyle}
        data-thumbnail={props.thumbnail ? true : undefined}
        data-selected={isCurrentPage}
        onWheel={onWheel}
      >
        {props.thumbnail && snapshotImage && false ? <img src={snapshotImage} className={css.snapshot}/> :
         props.thumbnail || !size
         ? contents
         : <Resizable className={css.resizable}
                      width={size.width}
                      height={size.height}
                      resizeHandles={resizeHandles}
                      onResize={onResize}>
           {contents}
         </Resizable>
        }
      </div>
    </PageViewStoreContext.Provider>)
})

export const PresentationPageContent = observer(function PresentationPageContent() {
  const {store: {pageNumber}, page} = usePresentationPageViewStore()

  if (!page) return null

  const {info} = page

  return (<div className={css.pageContent}>
    {info?.type === 'group' ? <PresentationPageGroupContent/>
                            : page.content ?? <span className={css.defaultContent}>Page {pageNumber}</span>}
  </div>)
})

/// endregion



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/PresentationMode/pageContents/PresentationPageView.tsx